import { Injectable, OnDestroy } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    of,
    combineLatest,
    Subscription
} from 'rxjs';

import { HttpClient } from '@angular/common/http';

import {
    delay,
    map,
    catchError,
    tap,
    take,
    filter,
    switchMap
} from 'rxjs/operators';

import { Product } from '../models/product.model';
import { CartItem } from '../models/cart-item.model';
import { AppliedCoupon } from '../models/applied-coupon.model';
import { environment } from '../environments/environment';

declare const bootstrap: any;

@Injectable({
    providedIn: 'root',
})
export class CartService implements OnDestroy {

    getDiscountAmount(): string | number {
        throw new Error('Method not implemented.');
    }

    private apiUrl =
        environment.apiUrl + 'api/get_all_coupons';

    private checkoutApiUrl =
        environment.apiUrl + 'checkout/';

    private enabledCouriersApiUrl =
        environment.apiUrl +
        'checkout/get_enabled_couriers';

    private cartItemsSubject =
        new BehaviorSubject<CartItem[]>([]);

    public cartItems$: Observable<CartItem[]> =
        this.cartItemsSubject.asObservable();

    private appliedCouponsSubject =
        new BehaviorSubject<AppliedCoupon[]>([]);

    public appliedCoupons$: Observable<AppliedCoupon[]> =
        this.appliedCouponsSubject.asObservable();

    private _allCouponsFromDatabase: AppliedCoupon[] = [];

    private couponsLoaded =
        new BehaviorSubject<boolean>(false);

    private _currentCustomerId:
        BehaviorSubject<number | null> =
        new BehaviorSubject<number | null>(101);

    public currentCustomerId$: Observable<number | null> =
        this._currentCustomerId.asObservable();

    private _manualOverrideCouponCode =
        new BehaviorSubject<string | null>(null);

    public manualOverrideCouponCode$: Observable<string | null> =
        this._manualOverrideCouponCode.asObservable();

    private _suppressAutoApply =
        new BehaviorSubject<boolean>(false);

    public suppressAutoApply$: Observable<boolean> =
        this._suppressAutoApply.asObservable();

    private _deliveryChargeSubject =
        new BehaviorSubject<number>(0);

    public deliveryCharge$ =
        this._deliveryChargeSubject.asObservable();


    /* ============================================================
       CUSTOMER
    ============================================================ */

    public updateCurrentCustomerId(
        id: number | null
    ): void {

        if (this._currentCustomerId.value !== id) {

            this._currentCustomerId.next(id);
        }
    }


    /* ============================================================
       PRODUCT PRICE
    ============================================================ */

    private getEffectiveProductPrice(
        product: Product
    ): number {

        const specialPriceNum =
            parseFloat(
                product.special_price as any
            );

        const mrpPriceNum =
            parseFloat(
                product.mrp_price as any
            );

        if (
            !isNaN(specialPriceNum) &&
            specialPriceNum > 0
        ) {

            return specialPriceNum;
        }

        if (!isNaN(mrpPriceNum)) {

            return mrpPriceNum;
        }

        return 0;
    }


    /* ============================================================
       ITEMS TOTAL
    ============================================================ */

    public readonly itemsTotal$: Observable<number> =
        this.cartItemsSubject
            .asObservable()
            .pipe(

                map((items) => {

                    return items.reduce(
                        (sum, item) =>
                            sum +
                            item.effectivePrice *
                            item.quantity,

                        0
                    );

                })

            );


    public readonly cartTotal$: Observable<number> =
        this.itemsTotal$;


    /* ============================================================
       BYOB DISCOUNT
       
       3 books = 0%
       4 books = 5%
       5+ books = 10%
       
       Discount is applied to the COMPLETE BYOB VALUE.
    ============================================================ */

    private getByobDiscountForItem(
        item: CartItem
    ): number {

        const product: any =
            item.product as any;

        const options =
            product?.options;

        /*
        |--------------------------------------------------------------------------
        | Check whether this is BYOB
        |--------------------------------------------------------------------------
        */

        if (
            !options ||
            options['is_byob_box'] !== true
        ) {

            return 0;
        }


        /*
        |--------------------------------------------------------------------------
        | Get selected books
        |--------------------------------------------------------------------------
        */

        const selectedBooks =
            options['selected_books'];


        const bookCount =
            Array.isArray(selectedBooks)
                ? selectedBooks.length
                : 0;


        /*
        |--------------------------------------------------------------------------
        | Determine discount percentage
        |--------------------------------------------------------------------------
        */

        let discountPercentage = 0;


        if (bookCount >= 5) {

            discountPercentage = 10;

        } else if (bookCount === 4) {

            discountPercentage = 5;

        } else {

            discountPercentage = 0;
        }


        /*
        |--------------------------------------------------------------------------
        | No BYOB discount
        |--------------------------------------------------------------------------
        */

        if (discountPercentage <= 0) {

            return 0;
        }


        /*
        |--------------------------------------------------------------------------
        | COMPLETE BYOB VALUE
        |--------------------------------------------------------------------------
        */

        const byobValue =
            Number(item.effectivePrice || 0) *
            Number(item.quantity || 1);


        if (byobValue <= 0) {

            return 0;
        }


        /*
        |--------------------------------------------------------------------------
        | Calculate discount
        |--------------------------------------------------------------------------
        */

        const discount =
            byobValue *
            (
                discountPercentage /
                100
            );


        return Number(
            discount.toFixed(2)
        );
    }


    /* ============================================================
       TOTAL COUPON + BYOB DISCOUNT
    ============================================================ */

    /* ============================================================
   TOTAL COUPON DISCOUNT
============================================================ */

public readonly totalCouponDiscount$: Observable<number> =
    combineLatest([

        this.appliedCouponsSubject.asObservable(),

        this.couponsLoaded.asObservable(),

        this.cartItemsSubject.asObservable(),

        this.currentCustomerId$,

    ]).pipe(

        map(
            ([
                appliedCoupons,
                loaded,
                cartItems,
                currentCustomerId
            ]) => {

                if (
                    !loaded ||
                    appliedCoupons.length === 0
                ) {

                    return 0;
                }


                /*
                |--------------------------------------------------------------------------
                | ONLY ONE COUPON CAN BE APPLIED
                |--------------------------------------------------------------------------
                */

                const applied =
                    appliedCoupons[0];


                const couponCode =
                    applied.coupon_code
                        .toUpperCase();


                /*
                |--------------------------------------------------------------------------
                | BYOB5 / BYOB10
                |--------------------------------------------------------------------------
                */

                if (
                    couponCode === 'BYOB5' ||
                    couponCode === 'BYOB10'
                ) {

                    const byobCoupon =
                        this.getByobCouponByCode(
                            couponCode
                        );


                    if (!byobCoupon) {

                        return 0;
                    }


                    const byobTotal =
                        this.getByobCartTotal();


                    if (
                        byobTotal <= 0
                    ) {

                        return 0;
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | BYOB5
                    |--------------------------------------------------------------------------
                    */

                    if (
                        couponCode === 'BYOB5'
                    ) {

                        const bookCount =
                            this.cartItemsSubject.value
                                .filter(
                                    item =>
                                        this.isByobItem(item)
                                )
                                .reduce(
                                    (
                                        max,
                                        item
                                    ) =>
                                        Math.max(
                                            max,
                                            this.getByobBookCount(
                                                item
                                            )
                                        ),
                                    0
                                );


                        if (
                            bookCount !== 4
                        ) {

                            return 0;
                        }
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | BYOB10
                    |--------------------------------------------------------------------------
                    */

                    if (
                        couponCode === 'BYOB10'
                    ) {

                        const bookCount =
                            this.cartItemsSubject.value
                                .filter(
                                    item =>
                                        this.isByobItem(item)
                                )
                                .reduce(
                                    (
                                        max,
                                        item
                                    ) =>
                                        Math.max(
                                            max,
                                            this.getByobBookCount(
                                                item
                                            )
                                        ),
                                    0
                                );


                        if (
                            bookCount < 5
                        ) {

                            return 0;
                        }
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | BYOB discount on COMPLETE BYOB value
                    |--------------------------------------------------------------------------
                    */

                    const discount =
                        byobTotal *
                        (
                            byobCoupon.discount_value /
                            100
                        );


                    return Number(
                        Math.min(
                            discount,
                            byobTotal
                        ).toFixed(2)
                    );
                }


                /*
                |--------------------------------------------------------------------------
                | NORMAL PRODUCT COUPON
                |--------------------------------------------------------------------------
                */

                const couponDefinition =
                    this._allCouponsFromDatabase.find(
                        coupon =>
                            coupon.coupon_code
                                .toUpperCase() ===
                            couponCode
                    );


                if (!couponDefinition) {

                    return 0;
                }


                const subtotalForCoupon =
                    this.getCartTotalForCoupon(
                        couponDefinition
                    );


                if (
                    subtotalForCoupon <= 0
                ) {

                    return 0;
                }


                /*
                |--------------------------------------------------------------------------
                | Expiry
                |--------------------------------------------------------------------------
                */

                const isExpired =
                    couponDefinition.expiry_date &&
                    new Date() >
                    new Date(
                        couponDefinition.expiry_date
                    );


                if (isExpired) {

                    return 0;
                }


                /*
                |--------------------------------------------------------------------------
                | Minimum order
                |--------------------------------------------------------------------------
                */

                const meetsMinOrder =
                    subtotalForCoupon >=
                    (
                        couponDefinition.min_order_value ||
                        0
                    );


                if (!meetsMinOrder) {

                    return 0;
                }


                /*
                |--------------------------------------------------------------------------
                | Customer restriction
                |--------------------------------------------------------------------------
                */

                if (
                    couponDefinition.visibility ===
                    'specific_customer'
                ) {

                    const allowedIds: number[] =
                        typeof couponDefinition
                            .allowed_customer_ids ===
                        'string'

                            ? couponDefinition
                                .allowed_customer_ids
                                .split(',')
                                .map(
                                    (
                                        id: string
                                    ) =>
                                        parseInt(
                                            id.trim(),
                                            10
                                        )
                                )
                                .filter(
                                    (
                                        id: number
                                    ) =>
                                        !isNaN(id)
                                )

                            : Array.isArray(
                                couponDefinition
                                    .allowed_customer_ids
                            )

                                ? couponDefinition
                                    .allowed_customer_ids

                                : [];


                    if (
                        currentCustomerId === null ||
                        !allowedIds.includes(
                            currentCustomerId as number
                        )
                    ) {

                        return 0;
                    }
                }


                /*
                |--------------------------------------------------------------------------
                | Calculate normal coupon
                |--------------------------------------------------------------------------
                */

                let discount = 0;


                if (
                    couponDefinition.discount_type ===
                    'fixed'
                ) {

                    discount =
                        couponDefinition.discount_value;

                } else if (
                    couponDefinition.discount_type ===
                    'percentage'
                ) {

                    discount =
                        subtotalForCoupon *
                        (
                            couponDefinition
                                .discount_value /
                            100
                        );
                }


                return Number(
                    Math.min(
                        discount,
                        subtotalForCoupon
                    ).toFixed(2)
                );
            }
        ),

        catchError(
            (err) => {

                console.error(
                    'Coupon discount calculation error:',
                    err
                );

                return of(0);
            }
        )
    );

    /* ============================================================
       SUB TOTAL AFTER DISCOUNT
    ============================================================ */

    public readonly subTotalAfterDiscount$: Observable<number> =
        combineLatest([

            this.itemsTotal$,

            this.totalCouponDiscount$,

        ]).pipe(

            map(
                ([
                    itemsTotal,
                    discount
                ]) =>

                    Math.max(
                        0,
                        itemsTotal -
                        discount
                    )
            )
        );


    /* ============================================================
       FINAL TOTAL
    ============================================================ */

    public readonly finalTotal$: Observable<number> =
        combineLatest([

            this.subTotalAfterDiscount$,

            this.deliveryCharge$,

        ]).pipe(

            map(
                ([
                    subTotal,
                    delivery
                ]) =>

                    subTotal +
                    delivery
            )
        );


    private _autoApplySubscription:
        Subscription | null = null;

    private _initSubscription:
        Subscription | null = null;


    /* ============================================================
       CONSTRUCTOR
    ============================================================ */

    constructor(
        private http: HttpClient
    ) {

        this._initSubscription =
            this.fetchCouponsFromBackend()

                .pipe(

                    tap(
                        () =>
                            this.couponsLoaded.next(
                                true
                            )
                    ),

                    tap(
                        () =>
                            this.loadCartFromLocalStorage()
                    ),

                    tap(
                        () =>
                            this.loadCouponsFromLocalStorage()
                    ),

                    take(1)
                )

                .subscribe(

                    () => {

                        this.applyBestAvailableCoupon();

                        this.setupAutoApplyListeners();
                    },

                    (error) =>
                        console.error(error)
                );
    }


    /* ============================================================
       DESTROY
    ============================================================ */

    ngOnDestroy(): void {

        if (this._autoApplySubscription) {

            this._autoApplySubscription.unsubscribe();
        }

        if (this._initSubscription) {

            this._initSubscription.unsubscribe();
        }
    }


    /* ============================================================
       AUTO APPLY LISTENERS
    ============================================================ */

    private setupAutoApplyListeners(): void {

        if (this._autoApplySubscription) {

            this._autoApplySubscription.unsubscribe();
        }


        this._autoApplySubscription =
            combineLatest([

                this.cartItems$,

                this._currentCustomerId.asObservable(),

                this._manualOverrideCouponCode.asObservable(),

            ])

                .pipe(

                    filter(
                        () =>
                            this.couponsLoaded.value
                    ),

                    delay(0),

                    tap(
                        () =>
                            this.applyBestAvailableCoupon()
                    )
                )

                .subscribe();
    }


    /* ============================================================
       FETCH COUPONS
    ============================================================ */

    private fetchCouponsFromBackend():
        Observable<AppliedCoupon[]> {

        return this.http
            .get<AppliedCoupon[]>(
                this.apiUrl
            )

            .pipe(

                map(
                    (coupons) => {

                        return coupons.map(
                            (coupon) => ({

                                ...coupon,

                                expiry_date:
                                    coupon.expiry_date
                                        ? new Date(
                                            coupon.expiry_date
                                        )
                                        : undefined,

                                allowed_customer_ids:
                                    typeof coupon.allowed_customer_ids ===
                                    'string'

                                        ? coupon
                                            .allowed_customer_ids
                                            .split(',')
                                            .map(
                                                (
                                                    id: string
                                                ) =>
                                                    parseInt(
                                                        id.trim(),
                                                        10
                                                    )
                                            )
                                            .filter(
                                                (
                                                    id: number
                                                ) =>
                                                    !isNaN(id)
                                            )

                                        : Array.isArray(
                                            coupon.allowed_customer_ids
                                        )

                                            ? coupon.allowed_customer_ids

                                            : null,

                                allowed_product_ids:
                                    coupon.allowed_product_ids ===
                                    null

                                        ? null

                                        : typeof coupon.allowed_product_ids ===
                                          'string'

                                            ? coupon
                                                .allowed_product_ids
                                                .split(',')
                                                .map(
                                                    (
                                                        id: string
                                                    ) =>
                                                        parseInt(
                                                            id.trim(),
                                                            10
                                                        )
                                                )
                                                .filter(
                                                    (
                                                        id: number
                                                    ) =>
                                                        !isNaN(id)
                                                )

                                            : Array.isArray(
                                                coupon.allowed_product_ids
                                            )

                                                ? coupon.allowed_product_ids

                                                : null,

                            })
                        );
                    }
                ),

                tap(
                    (processedCoupons) => {

                        this._allCouponsFromDatabase =
                            processedCoupons;
                    }
                ),

                catchError(
                    (error) => {

                        console.error(
                            'Failed to fetch coupons:',
                            error
                        );

                        this._allCouponsFromDatabase =
                            [];

                        return of([]);
                    }
                )
            );
    }


    /* ============================================================
       VISIBLE COUPONS
    ============================================================ */

    /* ============================================================
   VISIBLE COUPONS
============================================================ */

public getVisibleCoupons():
    Observable<AppliedCoupon[]> {

    return combineLatest([

        this.couponsLoaded.pipe(
            filter(
                loaded =>
                    loaded
            )
        ),

        this.currentCustomerId$,

        this.cartItems$,

    ]).pipe(

        map(
            ([
                loaded,
                currentCustomerId,
                cartItems
            ]) => {

                /*
                |--------------------------------------------------------------------------
                | NORMAL COUPONS
                |--------------------------------------------------------------------------
                */

                const normalCoupons =
                    this._allCouponsFromDatabase
                        .filter(
                            coupon => {

                                /*
                                |--------------------------------------------------------------------------
                                | Expired
                                |--------------------------------------------------------------------------
                                */

                                const isExpired =
                                    coupon.expiry_date &&
                                    new Date() >
                                    new Date(
                                        coupon.expiry_date
                                    );


                                if (
                                    isExpired
                                ) {

                                    return false;
                                }


                                /*
                                |--------------------------------------------------------------------------
                                | Eligible product total
                                |--------------------------------------------------------------------------
                                */

                                const subtotalForCoupon =
                                    this.getCartTotalForCoupon(
                                        coupon
                                    );


                                if (
                                    subtotalForCoupon <= 0
                                ) {

                                    return false;
                                }


                                /*
                                |--------------------------------------------------------------------------
                                | Minimum order
                                |--------------------------------------------------------------------------
                                */

                                if (
                                    subtotalForCoupon <
                                    (
                                        coupon.min_order_value ||
                                        0
                                    )
                                ) {

                                    return false;
                                }


                                /*
                                |--------------------------------------------------------------------------
                                | Customer restriction
                                |--------------------------------------------------------------------------
                                */

                                if (
                                    coupon.visibility ===
                                    'specific_customer'
                                ) {

                                    if (
                                        currentCustomerId ===
                                        null
                                    ) {

                                        return false;
                                    }


                                    const allowedIds: number[] =
                                        Array.isArray(
                                            coupon.allowed_customer_ids
                                        )

                                            ? coupon
                                                .allowed_customer_ids

                                            : typeof coupon
                                                .allowed_customer_ids ===
                                              'string'

                                                ? coupon
                                                    .allowed_customer_ids
                                                    .split(',')
                                                    .map(
                                                        (
                                                            id: string
                                                        ) =>
                                                            parseInt(
                                                                id.trim(),
                                                                10
                                                            )
                                                    )
                                                    .filter(
                                                        (
                                                            id: number
                                                        ) =>
                                                            !isNaN(id)
                                                    )

                                                : [];


                                    if (
                                        !allowedIds.includes(
                                            currentCustomerId as number
                                        )
                                    ) {

                                        return false;
                                    }
                                }


                                return true;
                            }
                        );


                /*
                |--------------------------------------------------------------------------
                | BYOB COUPONS
                |--------------------------------------------------------------------------
                */

                const byobCoupons =
                    this.getAvailableByobCoupons();


                /*
                |--------------------------------------------------------------------------
                | SHOW BOTH
                |--------------------------------------------------------------------------
                */

                return [

                    ...normalCoupons,

                    ...byobCoupons

                ];
            }
        )
    );
}


    /* ============================================================
       COUPON ELIGIBILITY
    ============================================================ */

    public isCouponEligible(
        coupon: AppliedCoupon
    ): Observable<boolean> {

        return combineLatest([

            this.currentCustomerId$,

            this.cartItems$,

        ]).pipe(

            map(
                ([
                    currentCustomerId,
                    cartItems
                ]) => {

                    const isExpired =
                        coupon.expiry_date &&
                        new Date() >
                        new Date(
                            coupon.expiry_date
                        );


                    if (isExpired) {

                        return false;
                    }


                    const subtotalForCoupon =
                        this.getCartTotalForCoupon(
                            coupon
                        );


                    if (
                        subtotalForCoupon <= 0
                    ) {

                        return false;
                    }


                    const meetsMinOrder =
                        subtotalForCoupon >=
                        (
                            coupon.min_order_value ||
                            0
                        );


                    if (!meetsMinOrder) {

                        return false;
                    }


                    if (
                        coupon.visibility ===
                        'specific_customer'
                    ) {

                        if (
                            currentCustomerId ===
                            null
                        ) {

                            return false;
                        }


                        const allowedIds: number[] =
                            typeof coupon.allowed_customer_ids ===
                            'string'

                                ? coupon
                                    .allowed_customer_ids
                                    .split(',')
                                    .map(
                                        (
                                            id: string
                                        ) =>
                                            parseInt(
                                                id.trim(),
                                                10
                                            )
                                    )
                                    .filter(
                                        (
                                            id: number
                                        ) =>
                                            !isNaN(id)
                                    )

                                : Array.isArray(
                                    coupon.allowed_customer_ids
                                )

                                    ? coupon.allowed_customer_ids

                                    : [];


                        if (
                            !allowedIds.includes(
                                currentCustomerId as number
                            )
                        ) {

                            return false;
                        }
                    }


                    return true;
                }
            )
        );
    }


    /* ============================================================
       LOCAL STORAGE - CART
    ============================================================ */

    private saveCartToLocalStorage(): void {

        localStorage.setItem(
            'shopping_cart',

            JSON.stringify(
                this.cartItemsSubject.value
            )
        );
    }


    private loadCartFromLocalStorage(): void {

        const storedCart =
            localStorage.getItem(
                'shopping_cart'
            );


        if (storedCart) {

            try {

                const cartItems:
                    CartItem[] =
                    JSON.parse(
                        storedCart
                    );


                const validCartItems =
                    cartItems.filter(
                        (item) => {

                            const isValid =
                                item.product &&
                                item.product.id !==
                                undefined &&
                                item.quantity !==
                                undefined;


                            if (
                                isValid &&
                                item.effectivePrice ===
                                undefined
                            ) {

                                item.effectivePrice =
                                    this.getEffectiveProductPrice(
                                        item.product
                                    );
                            }


                            return isValid;
                        }
                    );


                this.cartItemsSubject.next(
                    validCartItems
                );

            } catch (e) {

                console.error(
                    'Failed to load cart:',
                    e
                );

                localStorage.removeItem(
                    'shopping_cart'
                );

                this.cartItemsSubject.next(
                    []
                );
            }
        }
    }


    /* ============================================================
       LOCAL STORAGE - COUPONS
    ============================================================ */

    private saveCouponsToLocalStorage(): void {

        const dataToStore = {

            applied:
                this.appliedCouponsSubject
                    .value
                    .map(
                        (coupon) => ({

                            ...coupon,

                            expiry_date:
                                coupon.expiry_date instanceof
                                Date

                                    ? coupon.expiry_date
                                        .toISOString()

                                    : undefined,

                            allowed_customer_ids:
                                Array.isArray(
                                    coupon.allowed_customer_ids
                                )

                                    ? coupon.allowed_customer_ids

                                    : coupon.allowed_customer_ids ||
                                      null,

                            allowed_product_ids:
                                Array.isArray(
                                    coupon.allowed_product_ids
                                )

                                    ? coupon.allowed_product_ids

                                    : coupon.allowed_product_ids ||
                                      null,

                        })
                    ),

            manualOverride:
                this._manualOverrideCouponCode.value,

            suppressAutoApply:
                this._suppressAutoApply.value,
        };


        localStorage.setItem(
            'applied_coupons_data',

            JSON.stringify(
                dataToStore
            )
        );
    }


    private loadCouponsFromLocalStorage(): void {

        const storedData =
            localStorage.getItem(
                'applied_coupons_data'
            );


        if (storedData) {

            try {

                const parsedData =
                    JSON.parse(
                        storedData
                    );


                const parsedCoupons:
                    AppliedCoupon[] =
                    parsedData.applied.map(
                        (coupon: any) => ({

                            ...coupon,

                            expiry_date:
                                coupon.expiry_date
                                    ? new Date(
                                        coupon.expiry_date
                                    )
                                    : undefined,

                            allowed_customer_ids:
                                typeof coupon.allowed_customer_ids ===
                                'string'

                                    ? coupon
                                        .allowed_customer_ids
                                        .split(',')
                                        .map(
                                            (
                                                id: string
                                            ) =>
                                                parseInt(
                                                    id.trim(),
                                                    10
                                                )
                                        )
                                        .filter(
                                            (
                                                id: number
                                            ) =>
                                                !isNaN(id)
                                        )

                                    : Array.isArray(
                                        coupon.allowed_customer_ids
                                    )

                                        ? coupon.allowed_customer_ids

                                        : null,

                            allowed_product_ids:
                                typeof coupon.allowed_product_ids ===
                                'string'

                                    ? coupon
                                        .allowed_product_ids
                                        .split(',')
                                        .map(
                                            (
                                                id: string
                                            ) =>
                                                parseInt(
                                                    id.trim(),
                                                    10
                                                )
                                        )
                                        .filter(
                                            (
                                                id: number
                                            ) =>
                                                !isNaN(id)
                                        )

                                    : Array.isArray(
                                        coupon.allowed_product_ids
                                    )

                                        ? coupon.allowed_product_ids

                                        : null,

                        })
                    );


                this._manualOverrideCouponCode.next(
                    parsedData.manualOverride ||
                    null
                );


                this._suppressAutoApply.next(
                    false
                );


                if (
                    parsedCoupons.length > 0
                ) {

                    const storedCouponCode =
                        parsedCoupons[0].coupon_code;


                    const couponDefinition =
                        this._allCouponsFromDatabase.find(
                            (c) =>
                                c.coupon_code ===
                                storedCouponCode
                        );


                    if (couponDefinition) {

                        this.appliedCouponsSubject.next(
                            [couponDefinition]
                        );

                    } else {

                        this.appliedCouponsSubject.next(
                            []
                        );

                        this.saveCouponsToLocalStorage();

                        this._manualOverrideCouponCode.next(
                            null
                        );

                        this._suppressAutoApply.next(
                            false
                        );
                    }

                } else {

                    this.appliedCouponsSubject.next(
                        []
                    );
                }

            } catch (e) {

                console.error(
                    'Failed to load coupons:',
                    e
                );

                this.appliedCouponsSubject.next(
                    []
                );

                this._manualOverrideCouponCode.next(
                    null
                );

                this._suppressAutoApply.next(
                    false
                );
            }

        } else {

            this.appliedCouponsSubject.next(
                []
            );

            this._manualOverrideCouponCode.next(
                null
            );

            this._suppressAutoApply.next(
                false
            );
        }
    }


    /* ============================================================
       ADD TO CART
    ============================================================ */

    addToCart(
        product: Product,
        quantity: number = 1
    ): void {

        const currentItems =
            [
                ...this.cartItemsSubject.value
            ];


        const existingItem =
            currentItems.find(
                (item) =>
                    item.product.id ===
                    product.id
            );


        const mrp_price =
            parseFloat(
                product.mrp_price
            ) || 0;


        const special_price =
            parseFloat(
                product.special_price
            ) || 0;


        const effectivePrice =
            this.getEffectiveProductPrice(
                product
            );


        if (existingItem) {

            /*
            |--------------------------------------------------------------------------
            | BYOB should normally be a separate custom box.
            |
            | Since BYOB uses id = 0, don't merge multiple BYOB boxes.
            |--------------------------------------------------------------------------
            */

            const existingOptions: any =
                (existingItem.product as any)
                    ?.options;

            const newOptions: any =
                (product as any)
                    ?.options;


            const existingIsByob =
                existingOptions?.is_byob_box === true;


            const newIsByob =
                newOptions?.is_byob_box === true;


            if (
                existingIsByob &&
                newIsByob
            ) {

                currentItems.push({

                    product,

                    quantity,

                    effectivePrice,

                    mrp_price,

                    special_price

                } as CartItem);

            } else {

                existingItem.quantity +=
                    quantity;

                existingItem.effectivePrice =
                    effectivePrice;
            }

        } else {

            currentItems.push({

                product,

                quantity,

                effectivePrice,

                mrp_price,

                special_price

            } as CartItem);
        }


        this.cartItemsSubject.next(
            currentItems
        );


        this._suppressAutoApply.next(
            false
        );


        this.saveCartToLocalStorage();


        this.openOffcanvasCart();
    }


    /* ============================================================
       REMOVE FROM CART
    ============================================================ */

    removeFromCart(
        productId: number
    ): void {

        let currentItems =
            this.cartItemsSubject.value.filter(
                (item) =>
                    item.product.id !==
                    productId
            );


        this.cartItemsSubject.next(
            [
                ...currentItems
            ]
        );


        this._suppressAutoApply.next(
            false
        );


        this.saveCartToLocalStorage();
    }


    /* ============================================================
       UPDATE QUANTITY
    ============================================================ */

    updateQuantity(
        productId: number,
        newQuantity: number
    ): void {

        const currentItems =
            this.cartItemsSubject.value;


        const itemToUpdate =
            currentItems.find(
                (item) =>
                    item.product.id ===
                    productId
            );


        if (itemToUpdate) {

            /*
            |--------------------------------------------------------------------------
            | BYOB quantity cannot be changed
            |--------------------------------------------------------------------------
            */

            const options: any =
                (itemToUpdate.product as any)
                    ?.options;


            if (
                options?.is_byob_box === true
            ) {

                return;
            }


            if (
                newQuantity > 0
            ) {

                itemToUpdate.quantity =
                    newQuantity;

            } else {

                this.removeFromCart(
                    productId
                );

                return;
            }
        }


        this.cartItemsSubject.next(
            [
                ...currentItems
            ]
        );


        this._suppressAutoApply.next(
            false
        );


        this.saveCartToLocalStorage();
    }


    /* ============================================================
       CART TOTAL BEFORE COUPONS
    ============================================================ */

    public getCartTotalBeforeCoupons():
        number {

        return this.cartItemsSubject.value
            .reduce(
                (sum, item) =>

                    sum +
                    item.effectivePrice *
                    item.quantity,

                0
            );
    }


    /* ============================================================
       COUPON ELIGIBLE CART TOTAL
    ============================================================ */

    private getCartTotalForCoupon(
        coupon: AppliedCoupon | undefined
    ): number {

        if (!coupon) {

            return 0;
        }


        const allowedProductIds =
            Array.isArray(
                coupon.allowed_product_ids
            )

                ? coupon.allowed_product_ids

                : typeof coupon.allowed_product_ids ===
                  'string'

                    ? coupon
                        .allowed_product_ids
                        .split(',')
                        .map(
                            (id) =>
                                parseInt(
                                    id.trim(),
                                    10
                                )
                        )
                        .filter(
                            (id) =>
                                !isNaN(id)
                        )

                    : null;


        /*
        |--------------------------------------------------------------------------
        | null = coupon not applicable
        |--------------------------------------------------------------------------
        */

        if (
            allowedProductIds === null
        ) {

            return 0;
        }


        /*
        |--------------------------------------------------------------------------
        | Empty array = complete cart
        |--------------------------------------------------------------------------
        */

        if (
            allowedProductIds.length === 0
        ) {

            return this.getCartTotalBeforeCoupons();
        }


        /*
        |--------------------------------------------------------------------------
        | Product-specific coupon
        |--------------------------------------------------------------------------
        */

        return this.cartItemsSubject.value

            .filter(
                (item) => {

                    /*
                    |--------------------------------------------------------------------------
                    | BYOB fake product has ID 0.
                    |
                    | Existing normal product coupon rules should
                    | not accidentally treat BYOB as a normal product.
                    |--------------------------------------------------------------------------
                    */

                    const productId =
                        Number(
                            item.product.id
                        );


                    return (
                        productId > 0 &&
                        allowedProductIds.includes(
                            productId
                        )
                    );
                }
            )

            .reduce(
                (sum, item) =>

                    sum +
                    item.effectivePrice *
                    item.quantity,

                0
            );
    }


    /* ============================================================
       COUPON ACTIVE
    ============================================================ */

public isCouponActive(
    couponCode: string
): boolean {

    const applied =
        this.appliedCouponsSubject.value;


    return (

        applied.length > 0 &&

        !!applied[0]?.coupon_code &&

        applied[0]
            .coupon_code
            .toUpperCase() ===

        couponCode
            .toUpperCase()
    );
}

/* ============================================================
   BYOB COUPON HELPERS
============================================================ */

private isByobItem(item: CartItem): boolean {

    const product: any =
        item.product as any;

    return (
        product?.options?.['is_byob_box'] === true
    );
}


/* ============================================================
   GET BYOB BOOK COUNT
============================================================ */

private getByobBookCount(
    item: CartItem
): number {

    if (!this.isByobItem(item)) {
        return 0;
    }

    const product: any =
        item.product as any;

    const selectedBooks =
        product?.options?.['selected_books'] || [];

    return Array.isArray(selectedBooks)
        ? selectedBooks.length
        : 0;
}


/* ============================================================
   GET BYOB TOTAL
============================================================ */

private getByobCartTotal(): number {

    return this.cartItemsSubject.value

        .filter(
            item =>
                this.isByobItem(item)
        )

        .reduce(
            (sum, item) => {

                return sum +
                    (
                        Number(
                            item.effectivePrice || 0
                        ) *
                        Number(
                            item.quantity || 1
                        )
                    );

            },
            0
        );
}


/* ============================================================
   GET BYOB DISCOUNT %
============================================================ */

private getByobDiscountPercentage(): number {

    const byobItems =
        this.cartItemsSubject.value
            .filter(
                item =>
                    this.isByobItem(item)
            );


    let percentage = 0;


    for (const item of byobItems) {

        const bookCount =
            this.getByobBookCount(item);


        /*
        |--------------------------------------------------------------------------
        | 5+ books = 10%
        |--------------------------------------------------------------------------
        */

        if (bookCount >= 5) {

            percentage =
                Math.max(
                    percentage,
                    10
                );

        }

        /*
        |--------------------------------------------------------------------------
        | Exactly 4 books = 5%
        |--------------------------------------------------------------------------
        */

        else if (bookCount === 4) {

            percentage =
                Math.max(
                    percentage,
                    5
                );
        }
    }


    return percentage;
}


/* ============================================================
   CREATE BYOB COUPONS
============================================================ */

private getAvailableByobCoupons(): AppliedCoupon[] {

    const byobItems = this.cartItemsSubject.value
        .filter(item => this.isByobItem(item));

    if (byobItems.length === 0) {
        return [];
    }

    const bookCount = Math.max(
        ...byobItems.map(item => this.getByobBookCount(item))
    );

    const coupons: AppliedCoupon[] = [];

    // 4 Books → 5% OFF
    if (bookCount === 4) {

        coupons.push({
            coupon_code: 'BYOB5',
            display_text: 'Get 5% OFF on your Build Your Own Box with 4 books',
            discount_type: 'percentage',
            discount_value: 5,
            min_order_value: 0,
            visibility: 'public',
            allowed_customer_ids: null,
            allowed_product_ids: []
        } as AppliedCoupon);
    }

    // 5+ Books → 10% OFF
    if (bookCount >= 5) {

        coupons.push({
            coupon_code: 'BYOB10',
            display_text: 'Get 10% OFF on your Build Your Own Box with 5 or more books',
            discount_type: 'percentage',
            discount_value: 10,
            min_order_value: 0,
            visibility: 'public',
            allowed_customer_ids: null,
            allowed_product_ids: []
        } as AppliedCoupon);
    }

    return coupons;
}

/* ============================================================
   GET BYOB COUPON BY CODE
============================================================ */

private getByobCouponByCode(
    couponCode: string
): AppliedCoupon | null {

    const code =
        couponCode
            .trim()
            .toUpperCase();


    const byobCoupons =
        this.getAvailableByobCoupons();


    return (
        byobCoupons.find(
            coupon =>
                coupon.coupon_code
                    .toUpperCase() === code
        ) || null
    );
}

    /* ============================================================
       APPLY COUPON
    ============================================================ */

    /* ============================================================
   APPLY COUPON
============================================================ */

public applyCouponByCode(
    couponCode: string
): {
    success: boolean;
    message: string;
} {

    const code =
        couponCode
            .trim()
            .toUpperCase();


    /*
    |--------------------------------------------------------------------------
    | CHECK BYOB COUPON FIRST
    |--------------------------------------------------------------------------
    */

    if (
        code === 'BYOB5' ||
        code === 'BYOB10'
    ) {

        const byobCoupon =
            this.getByobCouponByCode(
                code
            );


        if (!byobCoupon) {

            return {

                success: false,

                message:
                    this.getByobCouponErrorMessage(
                        code
                    )
            };
        }


        /*
        |--------------------------------------------------------------------------
        | Only one coupon at a time
        |--------------------------------------------------------------------------
        */

        this.appliedCouponsSubject.next(
            [
                byobCoupon
            ]
        );


        this._manualOverrideCouponCode.next(
            code
        );


        /*
        | Prevent automatic normal coupon from replacing
        | user's BYOB selection.
        */

        this._suppressAutoApply.next(
            true
        );


        this.saveCouponsToLocalStorage();


        return {

            success: true,

            message:
                `Coupon "${code}" applied successfully!`
        };
    }


    /*
    |--------------------------------------------------------------------------
    | NORMAL PRODUCT COUPON
    |--------------------------------------------------------------------------
    */

    const couponToApply =
        this._allCouponsFromDatabase.find(
            coupon =>
                coupon.coupon_code
                    .toUpperCase() ===
                code
        );


    if (!couponToApply) {

        return {

            success: false,

            message:
                `Coupon code "${couponCode}" is invalid.`
        };
    }


    /*
    |--------------------------------------------------------------------------
    | Eligible product total
    |--------------------------------------------------------------------------
    */

    const baseTotalForCoupon =
        this.getCartTotalForCoupon(
            couponToApply
        );


    const currentCustomerId =
        this._currentCustomerId.value;


    /*
    |--------------------------------------------------------------------------
    | Expiry
    |--------------------------------------------------------------------------
    */

    if (
        couponToApply.expiry_date &&
        new Date() >
        new Date(
            couponToApply.expiry_date
        )
    ) {

        return {

            success: false,

            message:
                `Coupon "${code}" has expired.`
        };
    }


    /*
    |--------------------------------------------------------------------------
    | Minimum order
    |--------------------------------------------------------------------------
    */

    if (
        baseTotalForCoupon <
        (
            couponToApply.min_order_value ||
            0
        )
    ) {

        return {

            success: false,

            message:
                `Coupon "${code}" requires a minimum order of ₹${(
                    couponToApply.min_order_value ||
                    0
                ).toFixed(2)} of eligible products.`
        };
    }


    /*
    |--------------------------------------------------------------------------
    | Customer restriction
    |--------------------------------------------------------------------------
    */

    if (
        couponToApply.visibility ===
        'specific_customer'
    ) {

        if (
            currentCustomerId === null
        ) {

            return {

                success: false,

                message:
                    `Coupon "${code}" requires a logged-in account.`
            };
        }


        const allowedIds: number[] =
            Array.isArray(
                couponToApply
                    .allowed_customer_ids
            )

                ? couponToApply
                    .allowed_customer_ids

                : typeof couponToApply
                    .allowed_customer_ids ===
                  'string'

                    ? couponToApply
                        .allowed_customer_ids
                        .split(',')
                        .map(
                            (
                                id: string
                            ) =>
                                parseInt(
                                    id.trim(),
                                    10
                                )
                        )
                        .filter(
                            (
                                id: number
                            ) =>
                                !isNaN(id)
                        )

                    : [];


        if (
            !allowedIds.includes(
                currentCustomerId as number
            )
        ) {

            return {

                success: false,

                message:
                    `Coupon "${code}" is not available for your account.`
            };
        }
    }


    /*
    |--------------------------------------------------------------------------
    | APPLY NORMAL COUPON
    |--------------------------------------------------------------------------
    */

    this.appliedCouponsSubject.next(
        [
            couponToApply
        ]
    );


    this._manualOverrideCouponCode.next(
        code
    );


    /*
    |--------------------------------------------------------------------------
    | Prevent auto coupon from replacing user's selection
    |--------------------------------------------------------------------------
    */

    this._suppressAutoApply.next(
        true
    );


    this.saveCouponsToLocalStorage();


    return {

        success: true,

        message:
            `Coupon "${code}" applied successfully!`
    };
}

    /* ============================================================
       REMOVE COUPON
    ============================================================ */

    public removeCoupon(
        couponCode: string
    ): void {

        const currentApplied =
            this.appliedCouponsSubject.value;


        const isCurrentlyApplied =
            currentApplied.length > 0 &&

            currentApplied[0]
                .coupon_code
                .toUpperCase() ===

            couponCode.toUpperCase();


        if (
            isCurrentlyApplied
        ) {

            this.appliedCouponsSubject.next(
                []
            );

            this._manualOverrideCouponCode.next(
                null
            );

            this._suppressAutoApply.next(
                true
            );

            this.saveCouponsToLocalStorage();
        }
    }


    /* ============================================================
       CLEAR CART
    ============================================================ */

    public clearCart(): void {

        this.cartItemsSubject.next(
            []
        );

        this.appliedCouponsSubject.next(
            []
        );

        this._manualOverrideCouponCode.next(
            null
        );

        this._suppressAutoApply.next(
            false
        );

        this.saveCartToLocalStorage();

        this.saveCouponsToLocalStorage();
    }


    /* ============================================================
       OFFCANVAS
    ============================================================ */

    private openOffcanvasCart(): void {

        const offcanvasElement =
            document.getElementById(
                'offcanvasRight'
            );


        if (offcanvasElement) {

            const bsOffcanvas =
                new bootstrap.Offcanvas(
                    offcanvasElement
                );


            bsOffcanvas.show();
        }
    }


    /* ============================================================
       AUTO APPLY BEST COUPON
    ============================================================ */

    public applyBestAvailableCoupon(): void {

        if (
            this._suppressAutoApply.value
        ) {

            return;
        }


        this.couponsLoaded

            .pipe(

                filter(
                    (loaded) =>
                        loaded
                ),

                switchMap(
                    () =>

                        combineLatest([

                            this.itemsTotal$,

                            this.allCouponsFromDatabase$,

                            this.currentCustomerId$,

                            this._manualOverrideCouponCode
                                .asObservable(),

                        ])
                ),

                take(1)

            )

            .subscribe(

                ([
                    itemsTotal,
                    allCoupons,
                    currentCustomerId,
                    manualOverrideCode
                ]) => {

                    const currentAppliedCoupon =
                        this.appliedCouponsSubject.value
                            .length > 0

                            ? this.appliedCouponsSubject
                                .value[0]

                            : null;


                    /*
                    |--------------------------------------------------------------------------
                    | Empty cart
                    |--------------------------------------------------------------------------
                    */

                    if (
                        itemsTotal === 0
                    ) {

                        if (
                            currentAppliedCoupon ||
                            manualOverrideCode
                        ) {

                            this.appliedCouponsSubject.next(
                                []
                            );

                            this._manualOverrideCouponCode.next(
                                null
                            );

                            this._suppressAutoApply.next(
                                false
                            );

                            this.saveCouponsToLocalStorage();
                        }


                        return;
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Manual coupon
                    |--------------------------------------------------------------------------
                    */

                    if (
                        manualOverrideCode
                    ) {

                        const manuallyAppliedCoupon =
                            allCoupons?.find(
                                (
                                    c: AppliedCoupon
                                ) =>
                                    c.coupon_code
                                        ?.toUpperCase() ===
                                    manualOverrideCode
                                        .toUpperCase()
                            );


                        if (
                            manuallyAppliedCoupon
                        ) {

                            const subtotalForManualCoupon =
                                this.getCartTotalForCoupon(
                                    manuallyAppliedCoupon
                                );


                            const isExpired =
                                manuallyAppliedCoupon.expiry_date &&
                                new Date() >
                                new Date(
                                    manuallyAppliedCoupon.expiry_date
                                );


                            const meetsMinOrder =
                                subtotalForManualCoupon >=
                                (
                                    manuallyAppliedCoupon
                                        .min_order_value ||
                                    0
                                );


                            let isCustomerAllowed =
                                true;


                            if (
                                manuallyAppliedCoupon
                                    .visibility ===
                                'specific_customer'
                            ) {

                                const allowedIds: number[] =
                                    typeof manuallyAppliedCoupon
                                        .allowed_customer_ids ===
                                    'string'

                                        ? manuallyAppliedCoupon
                                            .allowed_customer_ids
                                            .split(',')
                                            .map(
                                                (
                                                    id: string
                                                ) =>
                                                    parseInt(
                                                        id.trim(),
                                                        10
                                                    )
                                            )
                                            .filter(
                                                (
                                                    id: number
                                                ) =>
                                                    !isNaN(id)
                                            )

                                        : Array.isArray(
                                            manuallyAppliedCoupon
                                                .allowed_customer_ids
                                        )

                                            ? manuallyAppliedCoupon
                                                .allowed_customer_ids

                                            : [];


                                if (
                                    currentCustomerId ===
                                    null ||

                                    !allowedIds.includes(
                                        currentCustomerId as number
                                    )
                                ) {

                                    isCustomerAllowed =
                                        false;
                                }
                            }


                            if (
                                isExpired ||

                                subtotalForManualCoupon <=
                                0 ||

                                !meetsMinOrder ||

                                !isCustomerAllowed
                            ) {

                                this.appliedCouponsSubject.next(
                                    []
                                );

                                this._manualOverrideCouponCode.next(
                                    null
                                );

                                this._suppressAutoApply.next(
                                    true
                                );

                                this.saveCouponsToLocalStorage();

                            } else {

                                if (
                                    !currentAppliedCoupon ||

                                    currentAppliedCoupon
                                        .coupon_code !==
                                    manuallyAppliedCoupon
                                        .coupon_code
                                ) {

                                    this.appliedCouponsSubject.next(
                                        [
                                            manuallyAppliedCoupon
                                        ]
                                    );

                                    this.saveCouponsToLocalStorage();
                                }


                                return;
                            }

                        } else {

                            this.appliedCouponsSubject.next(
                                []
                            );

                            this._manualOverrideCouponCode.next(
                                null
                            );

                            this._suppressAutoApply.next(
                                true
                            );

                            this.saveCouponsToLocalStorage();
                        }
                    }


                    if (
                        this._suppressAutoApply.value
                    ) {

                        return;
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Find best automatic coupon
                    |--------------------------------------------------------------------------
                    */

                    let bestAutoCoupon:
                        AppliedCoupon | null =
                        null;


                    let maxDiscount = 0;


                    const applicableCoupons =
                        allCoupons?.filter(
                            (
                                coupon: AppliedCoupon
                            ) => {

                                const isExpired =
                                    coupon.expiry_date &&
                                    new Date() >
                                    new Date(
                                        coupon.expiry_date
                                    );


                                if (
                                    isExpired
                                ) {

                                    return false;
                                }


                                const subtotalForCoupon =
                                    this.getCartTotalForCoupon(
                                        coupon
                                    );


                                if (
                                    subtotalForCoupon <=
                                    0
                                ) {

                                    return false;
                                }


                                const meetsMinOrder =
                                    subtotalForCoupon >=
                                    (
                                        coupon.min_order_value ||
                                        0
                                    );


                                if (
                                    !meetsMinOrder
                                ) {

                                    return false;
                                }


                                if (
                                    coupon.visibility ===
                                    'specific_customer'
                                ) {

                                    if (
                                        currentCustomerId ===
                                        null
                                    ) {

                                        return false;
                                    }


                                    const allowedIds: number[] =
                                        Array.isArray(
                                            coupon.allowed_customer_ids
                                        )

                                            ? coupon.allowed_customer_ids

                                            : typeof coupon.allowed_customer_ids ===
                                              'string'

                                                ? coupon
                                                    .allowed_customer_ids
                                                    .split(',')
                                                    .map(
                                                        (
                                                            id: string
                                                        ) =>
                                                            parseInt(
                                                                id.trim(),
                                                                10
                                                            )
                                                    )
                                                    .filter(
                                                        (
                                                            id: number
                                                        ) =>
                                                            !isNaN(id)
                                                    )

                                                : [];


                                    if (
                                        !allowedIds.includes(
                                            currentCustomerId as number
                                        )
                                    ) {

                                        return false;
                                    }
                                }


                                return true;
                            }
                        ) || [];


                    /*
                    |--------------------------------------------------------------------------
                    | Calculate best coupon
                    |--------------------------------------------------------------------------
                    */

                    for (
                        const coupon of
                        applicableCoupons
                    ) {

                        let currentCouponDiscount =
                            0;


                        const subtotalForCoupon =
                            this.getCartTotalForCoupon(
                                coupon
                            );


                        if (
                            coupon.discount_type ===
                            'fixed'
                        ) {

                            currentCouponDiscount =
                                coupon.discount_value;

                        } else if (
                            coupon.discount_type ===
                            'percentage'
                        ) {

                            currentCouponDiscount =
                                (
                                    subtotalForCoupon *
                                    coupon.discount_value
                                ) /
                                100;
                        }


                        currentCouponDiscount =
                            Math.min(
                                currentCouponDiscount,
                                subtotalForCoupon
                            );


                        if (
                            currentCouponDiscount >
                            maxDiscount
                        ) {

                            maxDiscount =
                                currentCouponDiscount;

                            bestAutoCoupon =
                                coupon;
                        }
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Apply best coupon
                    |--------------------------------------------------------------------------
                    */

                    if (
                        bestAutoCoupon
                    ) {

                        if (
                            !currentAppliedCoupon ||

                            currentAppliedCoupon
                                .coupon_code !==
                            bestAutoCoupon
                                .coupon_code
                        ) {

                            this.appliedCouponsSubject.next(
                                [
                                    bestAutoCoupon
                                ]
                            );

                            this.saveCouponsToLocalStorage();
                        }

                    } else if (
                        currentAppliedCoupon
                    ) {

                        this.appliedCouponsSubject.next(
                            []
                        );

                        this.saveCouponsToLocalStorage();
                    }
                }
            );
    }


    /* ============================================================
       ORDER DETAILS
    ============================================================ */

    getOrderDetails(
        orderId: string
    ): Observable<any> {

        const url =
            `${this.checkoutApiUrl}get_order_details/${orderId}`;


        return this.http
            .get<any>(url)

            .pipe(

                catchError(
                    (error) => {

                        console.error(
                            'Failed to load order details:',
                            error
                        );


                        return of({

                            success: false,

                            message:
                                'Failed to load order details.',

                            error,

                        });
                    }
                )
            );
    }


    /* ============================================================
       CURRENT COUPON
    ============================================================ */

    public getCurrentlyAppliedCoupon():
        Observable<AppliedCoupon | null> {

        return this.appliedCoupons$

            .pipe(

                map(
                    (coupons) =>
                        coupons.length > 0
                            ? coupons[0]
                            : null
                )
            );
    }


    /* ============================================================
       ALL COUPONS
    ============================================================ */

    public get allCouponsFromDatabase$():
        Observable<AppliedCoupon[]> {

        return this.couponsLoaded

            .pipe(

                filter(
                    (loaded) =>
                        loaded
                ),

                map(
                    () =>
                        this._allCouponsFromDatabase
                )
            );
    }


    /* ============================================================
       REFRESH COUPONS
    ============================================================ */

    public refreshCoupons(): void {

        this.fetchCouponsFromBackend()

            .pipe(
                take(1)
            )

            .subscribe({

                next: () => {

                    this.couponsLoaded.next(
                        true
                    );

                    this.applyBestAvailableCoupon();
                },

                error: (err) => {

                    console.error(
                        'Failed to refresh coupons',
                        err
                    );
                }

            });
    }


    /* ============================================================
       ENABLED COURIERS
    ============================================================ */

    public getEnabledCouriers(
        data: any
    ): Observable<any> {

        const headers = {

            'Content-Type':
                'application/json'

        };


        return this.http

            .post<any>(
                this.enabledCouriersApiUrl,
                data,
                {
                    headers
                }
            )

            .pipe(

                catchError(
                    (error) => {

                        console.error(
                            'API Error fetching couriers:',
                            error
                        );


                        return of({

                            status:
                                'error',

                            message:
                                'An error occurred while fetching couriers.',
                        });
                    }
                )
            );
    }


    /* ============================================================
       DELIVERY CHARGE
    ============================================================ */

    public setDeliveryCharge(
        charge: number
    ): void {

        this._deliveryChargeSubject.next(
            charge
        );
    }

    /* ============================================================
   BYOB COUPON ERROR
============================================================ */

private getByobCouponErrorMessage(
    couponCode: string
): string {

    const byobItems =
        this.cartItemsSubject.value
            .filter(
                item =>
                    this.isByobItem(item)
            );


    if (
        byobItems.length === 0
    ) {

        return 'BYOB coupon can only be used with a Build Your Own Box.';
    }


    const bookCount =
        Math.max(
            ...byobItems.map(
                item =>
                    this.getByobBookCount(item)
            )
        );


    if (
        couponCode === 'BYOB5'
    ) {

        if (
            bookCount < 4
        ) {

            return 'BYOB5 is available when you select 4 books.';
        }


        if (
            bookCount > 4
        ) {

            return 'BYOB5 is available only for 4 books. For 5 or more books, use BYOB10.';
        }
    }


    if (
        couponCode === 'BYOB10'
    ) {

        if (
            bookCount < 5
        ) {

            return 'BYOB10 is available when you select 5 or more books.';
        }
    }


    return 'This BYOB coupon is not applicable to your cart.';
}
}